import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Switch, message, Space, Popconfirm } from 'antd';
import FeatherIcon from 'feather-icons-react';
import { PageHeader } from '../../components/page-headers/page-headers';
import { Main, TableWrapper } from '../styled';
import { Cards } from '../../components/cards/frame/cards-frame';
import { DataService } from '../../config/dataService/dataService';
import { API } from '../../config/api/index';

const Sucursales = () => {
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSucursal, setEditingSucursal] = useState(null);
  const [form] = Form.useForm();

  const fetchSucursales = async () => {
    setLoading(true);
    try {
      const response = await DataService.get(API.pos.sucursales);
      setSucursales(response.data.results || []);
    } catch (error) {
      message.error('Error al cargar sucursales');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSucursales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async values => {
    try {
      if (editingSucursal) {
        await DataService.patch(`${API.pos.sucursales}/${editingSucursal.id}`, values);
        message.success('Sucursal actualizada correctamente');
      } else {
        await DataService.post(API.pos.sucursales, values);
        message.success('Sucursal creada correctamente');
      }

      setModalVisible(false);
      setEditingSucursal(null);
      form.resetFields();
      fetchSucursales();
    } catch (error) {
      const errorMsg = error?.response?.data?.message || 'Error al guardar sucursal';
      message.error(errorMsg);
    }
  };

  const handleEdit = sucursal => {
    setEditingSucursal(sucursal);
    form.setFieldsValue(sucursal);
    setModalVisible(true);
  };

  const handleCreate = () => {
    setEditingSucursal(null);
    form.resetFields();
    form.setFieldsValue({ activo: true });
    setModalVisible(true);
  };

  const handleToggleActive = async sucursal => {
    try {
      await DataService.patch(`${API.pos.sucursales}/${sucursal.id}`, {
        activo: !sucursal.activo,
      });
      message.success(`Sucursal ${sucursal.activo ? 'desactivada' : 'activada'} correctamente`);
      fetchSucursales();
    } catch (error) {
      message.error('Error al actualizar estado');
    }
  };

  const handleDelete = async sucursalId => {
    try {
      await DataService.delete(`${API.pos.sucursales}/${sucursalId}`);
      message.success('Sucursal eliminada correctamente');
      fetchSucursales();
    } catch (error) {
      message.error('Error al eliminar sucursal');
    }
  };

  const columns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 100,
      render: codigo => <strong>{codigo}</strong>,
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 200,
    },
    {
      title: 'Dirección',
      dataIndex: 'direccion',
      key: 'direccion',
      ellipsis: true,
    },
    {
      title: 'Teléfono',
      dataIndex: 'telefono',
      key: 'telefono',
      width: 130,
    },
    {
      title: 'Estado',
      dataIndex: 'activo',
      key: 'activo',
      width: 120,
      align: 'center',
      render: (activo, record) => (
        <Switch
          checked={activo}
          onChange={() => handleToggleActive(record)}
          checkedChildren="Activa"
          unCheckedChildren="Inactiva"
        />
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<FeatherIcon icon="edit" size={16} />}
            onClick={() => handleEdit(record)}
            title="Editar"
          />
          <Popconfirm
            title="¿Estás seguro de eliminar esta sucursal?"
            description="Esta acción no se puede deshacer."
            onConfirm={() => handleDelete(record.id)}
            okText="Sí, eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" danger icon={<FeatherIcon icon="trash-2" size={16} />} title="Eliminar" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        ghost
        title="Gestión de Sucursales"
        subTitle="Administra las diferentes ubicaciones del negocio"
        buttons={[
          <Button key="create" type="primary" onClick={handleCreate} icon={<FeatherIcon icon="plus" size={14} />}>
            Nueva Sucursal
          </Button>,
        ]}
      />

      <Main>
        <Cards headless>
          <TableWrapper className="table-responsive">
            <Table
              dataSource={sucursales}
              columns={columns}
              loading={loading}
              rowKey="id"
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} sucursales`,
              }}
            />
          </TableWrapper>
        </Cards>
      </Main>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FeatherIcon icon="map-pin" size={20} />
            <span>{editingSucursal ? 'Editar Sucursal' : 'Nueva Sucursal'}</span>
          </div>
        }
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingSucursal(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ activo: true }}>
          <Form.Item
            name="codigo"
            label="Código"
            rules={[
              { required: true, message: 'El código es requerido' },
              { pattern: /^[A-Z0-9]+$/, message: 'Solo mayúsculas y números sin espacios' },
              { max: 10, message: 'Máximo 10 caracteres' },
            ]}
            extra="Ejemplo: MATRIZ, SUC1, SUC2"
          >
            <Input
              placeholder="Código único (ej: MATRIZ, SUC1)"
              maxLength={10}
              style={{ textTransform: 'uppercase' }}
              onChange={e => {
                form.setFieldsValue({ codigo: e.target.value.toUpperCase() });
              }}
            />
          </Form.Item>

          <Form.Item
            name="nombre"
            label="Nombre de la Sucursal"
            rules={[
              { required: true, message: 'El nombre es requerido' },
              { min: 3, message: 'Mínimo 3 caracteres' },
              { max: 100, message: 'Máximo 100 caracteres' },
            ]}
          >
            <Input placeholder="Ej: Latido Café Matriz, Latido Centro" maxLength={100} />
          </Form.Item>

          <Form.Item name="direccion" label="Dirección" rules={[{ max: 250, message: 'Máximo 250 caracteres' }]}>
            <Input.TextArea rows={2} placeholder="Dirección completa de la sucursal" maxLength={250} showCount />
          </Form.Item>

          <Form.Item
            name="telefono"
            label="Teléfono"
            rules={[
              { pattern: /^[\d\s\-()+ ]*$/, message: 'Solo números, espacios, guiones y paréntesis' },
              { max: 20, message: 'Máximo 20 caracteres' },
            ]}
          >
            <Input placeholder="555-1234 o (55) 5555-1234" maxLength={20} />
          </Form.Item>

          <Form.Item name="activo" label="Estado" valuePropName="checked">
            <Switch checkedChildren="Activa" unCheckedChildren="Inactiva" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setEditingSucursal(null);
                  form.resetFields();
                }}
              >
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" icon={<FeatherIcon icon="save" size={14} />}>
                {editingSucursal ? 'Actualizar' : 'Crear Sucursal'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Sucursales;
